import { motion } from "framer-motion";
import { Check } from "lucide-react";
import SEO from "@/components/SEO";
import TrustMarquee from "@/components/TrustMarquee";
import { FadeUp, Chapter, CTABand, EASE } from "@/components/Reveal";
import { Pricing } from "@/components/Sections";

const INCLUDED = [
  "EP-W woningopname op een afspraakmoment dat u uitkomt",
  "Verwerking en registratie van het label in EP-online",
  "Uitleg over het resultaat en de geldigheid van tien jaar",
  "Persoonlijke begeleiding van aanvraag tot oplevering",
];

export default function Tarieven() {
  return (
    <>
      <SEO
        title="Tarieven energielabel woning | Directelabels Rotterdam"
        description="Bekijk de introductietarieven van Directelabels: energielabel appartement vanaf €139, tussenwoning €149, hoekwoning €159, 2-onder-1-kap €169 en vrijstaand €199, inclusief btw en registratie."
        path="/tarieven"
      />

      <section data-testid="tarieven-hero" className="mx-auto max-w-7xl px-5 pb-8 pt-14 md:px-8 md:pt-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs font-bold uppercase tracking-[0.24em] text-primary"
        >
          Tarieven
        </motion.p>
        <h1 data-testid="tarieven-title" className="mt-6 font-display text-4xl font-semibold leading-tight tracking-tighter text-ink md:text-6xl">
          {["Duidelijke tarieven,", "geen verrassingen."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
              >
                {i === 1 ? <span className="text-primary">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
        >
          Vaste vanaf-prijzen per woningtype, inclusief btw, woningopname en registratie in EP-online. Kies uw
          woningtype en vraag direct uw energielabel aan.
        </motion.p>
      </section>

      <Pricing number="01" />

      <TrustMarquee />

      <section data-testid="tarieven-included" className="border-y border-black/5 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Chapter
                number="02"
                label="Altijd inbegrepen"
                title="Wat zit er bij elke opname in?"
                description="Welk woningtype u ook kiest: de basis is altijd hetzelfde. Zorgvuldig, transparant en volledig ontzorgd."
              />
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-4">
                {INCLUDED.map((item, i) => (
                  <FadeUp key={item} delay={i * 0.08}>
                    <li data-testid={`included-item-${i}`} className="flex items-start gap-4 rounded-2xl border border-black/5 bg-background p-5">
                      <span className="font-display text-sm font-light text-primary">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm font-semibold leading-relaxed text-ink md:text-base">{item}</span>
                    </li>
                  </FadeUp>
                ))}
              </ul>
              <FadeUp delay={0.35}>
                <div className="mt-8 rounded-2xl border border-primary/20 bg-energy-pale/60 p-6">
                  <p className="text-sm leading-relaxed text-ink/80">
                    <Check size={15} strokeWidth={3} className="mr-2 inline text-primary" />
                    <strong className="font-semibold text-ink">Zakelijke klant?</strong> Voor meerdere woningen,
                    terugkerende opdrachten en structurele samenwerkingen maken wij graag een passend voorstel.
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
